import React, { useContext, useEffect, ComponentType, useState } from 'react';
import { UserDetailsContext } from '@/modules/RegisterLayout/contexts/UserDetailsContextProvider';
import { Loader } from '@pw-tech/omni-ui';
import { useRouter } from 'next/router';
import { webSDK } from '@/utils/webSdk';
import { Storage } from '@pw-tech/web-sdk';

// Types for better TypeScript support
interface WithAuthProps {
  [key: string]: any;
}
const withAuth = <P extends WithAuthProps,>(WrappedComponent: ComponentType<P>) => {
  const AuthenticatedComponent = (props: P) => {
    const {
      setUser
    } = useContext(UserDetailsContext);
    const router = useRouter();
    const [isClient, setIsClient] = useState(false);
    const [user, setUserState] = useState<any>(null);

    // Ensure we're on the client side
    useEffect(() => {
      setIsClient(true);
      if (webSDK?.user?.id) {
        setUserState(webSDK.user);
      }
    }, []);

    // Set user data in context when available
    useEffect(() => {
      if (user?.id) {
        setUser(user);
      }
    }, [user, setUser]);

    // Handle redirect after API response (success or error)
    useEffect(() => {
      if (!isClient) return; // Don't redirect during SSR

      const shouldRedirect = !user?.id;
      if (shouldRedirect) {
        if (router.pathname === '/register') {
          const queryString = new URLSearchParams(Object.entries(router.query).flatMap(([key, value]) => {
            if (value === undefined) return [];
            return Array.isArray(value) ? value.map(item => [key, item]) : [[key, value]];
          })).toString();
          const redirectPath = queryString ? `/register?${queryString}` : '/register';
          Storage.set('redirectTo', redirectPath);
        }
        router.replace({
          pathname: '/'
        });
      }
    }, [user, router, isClient]);

    // Show loading during SSR and while checking authentication
    if (!isClient || !user?.id) {
      return <div className="flex justify-center items-center h-screen">
          <div className="text-center">
            <Loader message="Authenticating..." />
          </div>
        </div>;
    }

    // Render the wrapped component only if authenticated
    return <WrappedComponent {...props} data-sentry-element="WrappedComponent" data-sentry-component="AuthenticatedComponent" data-sentry-source-file="withAuth.tsx" />;
  };

  // Add display name for better debugging
  AuthenticatedComponent.displayName = `withAuth(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;
  return AuthenticatedComponent;
};
export default withAuth;